import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiProblem, apiFetch, newIdempotencyKey } from './api.ts';
import { useSession } from './session.tsx';

/**
 * Auftrag aus PDF (Story 9.1): PDF-Bestellung hochladen und nachfragen, bis die KI fertig gelesen hat. Gemeinsam für die
 * Seite „Auftragsentwürfe“ und das Ziehen einer PDF auf jede Seite. Der Browser prüft nur „ist eine PDF“ (gleicher Satz
 * wie der Server); alles Weitere entscheidet der Server.
 * Story 9.14: neben PDF auch Fotos (JPG, PNG, WEBP) und Excel (.xlsx) – der Browser prüft nur Name bzw. Typ.
 */

export interface PdfOrderImport {
  id: string;
  status: 'reading' | 'draft_created' | 'failed';
  failure: string | null;
  orderId: string | null;
  orderNumber: string | null;
  createdAt: string;
}

export const PDF_UPLOAD_TEXTS = {
  notPdf: 'Bitte eine PDF, ein Foto (JPG, PNG, WEBP) oder eine Excel-Datei (.xlsx) wählen.',
  sending: 'Bestellung wird hochgeladen …',
  reading: 'Die KI liest die Bestellung … Der Entwurf erscheint gleich in der Liste.',
  pollFailed: 'Der Stand ist gerade nicht abrufbar – es wird weiter nachgefragt. Der Entwurf erscheint trotzdem in der Liste der Auftragsentwürfe.',
  created: (number: string) => `Auftragsentwurf ${number} angelegt – nichts reserviert, keine Auftragsbestätigung.`,
  unreadable: 'Die Datei ließ sich nicht lesen. Bitte noch einmal versuchen.',
  failed: 'Etwas ist schiefgelaufen. Bitte noch einmal versuchen.',
  open: 'Entwurf öffnen',
  /** Story 9.4: zur Maske „Auftrag anlegen“ im PDF-Modus. */
  review: 'Prüfen und freigeben',
} as const;

/** Dateien, die „Bestellung einlesen“ annimmt (Auswahl-Dialog `accept`). */
export const ORDER_FILE_ACCEPT = '.pdf,application/pdf,.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const ORDER_FILE_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']);

/** Name oder Typ sagt „PDF, Foto oder Excel“; der Server prüft zusätzlich den Inhalt. */
export function looksLikeOrderFile(file: File): boolean {
  return ORDER_FILE_TYPES.has(file.type) || /\.(?:pdf|png|jpe?g|webp|xlsx)$/i.test(file.name);
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result ?? '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('read'));
    reader.readAsDataURL(file);
  });
}

export type PdfUploadState =
  | { kind: 'idle' }
  | { kind: 'sending'; fileName: string }
  | { kind: 'reading'; fileName: string; importId: string; pollFailed?: boolean }
  | { kind: 'created'; fileName: string; orderId: string; orderNumber: string }
  | { kind: 'error'; text: string };

type UploadPhase = Exclude<PdfUploadState, { kind: 'created' }>;

/** Hochladen + Nachfragen (alle 1,5 s, solange die KI liest). Neue Datei während einer laufenden: neue Runde. */
export function usePdfOrderUpload() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<UploadPhase>({ kind: 'idle' });
  const importId = phase.kind === 'reading' ? phase.importId : null;

  const poll = useQuery({
    queryKey: ['pdf-import', importId],
    queryFn: () => apiFetch<PdfOrderImport>(getToken, `/api/v1/orders/pdf-imports/${importId}`),
    enabled: importId !== null,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'reading' ? false : 1500),
    // Auch nach einem Fehler weiter nachfragen (Intervall läuft weiter).
    refetchIntervalInBackground: true,
  });

  // Stand aus der letzten Antwort ableiten (kein zweiter Zustand).
  const data = phase.kind === 'reading' && poll.data?.id === phase.importId ? poll.data : null;
  let state: PdfUploadState = phase;
  // Abfrage scheitert (z. B. Verbindung weg): Hinweis statt stummem „liest …“; nachgefragt wird weiter.
  if (phase.kind === 'reading' && poll.isError && !data) state = { ...phase, pollFailed: true };
  if (phase.kind === 'reading' && data && data.status !== 'reading') {
    state =
      data.status === 'draft_created' && data.orderId && data.orderNumber
        ? { kind: 'created', fileName: phase.fileName, orderId: data.orderId, orderNumber: data.orderNumber }
        : { kind: 'error', text: data.failure ?? PDF_UPLOAD_TEXTS.failed };
  }

  // Liste der Auftragsentwürfe und Zähler neu laden, sobald ein Entwurf da ist.
  const createdId = state.kind === 'created' ? state.orderId : null;
  useEffect(() => {
    if (!createdId) return;
    void queryClient.invalidateQueries({ queryKey: ['list-rows'] });
    void queryClient.invalidateQueries({ queryKey: ['list-metrics'] });
  }, [createdId, queryClient]);

  // Laufende Nummer: nur die Antwort des zuletzt gestarteten Uploads ändert die Anzeige (z. B. zweimal schnell gezogen).
  const latest = useRef(0);
  const upload = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      const run = ++latest.current;
      const set = (next: UploadPhase) => {
        if (run === latest.current) setPhase(next);
      };
      if (!looksLikeOrderFile(file)) {
        set({ kind: 'error', text: PDF_UPLOAD_TEXTS.notPdf });
        return;
      }
      set({ kind: 'sending', fileName: file.name });
      let contentBase64: string;
      try {
        contentBase64 = await readBase64(file);
      } catch {
        set({ kind: 'error', text: PDF_UPLOAD_TEXTS.unreadable });
        return;
      }
      try {
        const created = await apiFetch<PdfOrderImport>(getToken, '/api/v1/orders/pdf-imports', {
          method: 'POST',
          body: { fileName: file.name, contentBase64 },
          idempotencyKey: newIdempotencyKey(),
        });
        set({ kind: 'reading', fileName: file.name, importId: created.id });
      } catch (err) {
        set({ kind: 'error', text: err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : PDF_UPLOAD_TEXTS.failed });
      }
    },
    [getToken],
  );

  const reset = useCallback(() => setPhase({ kind: 'idle' }), []);
  return { state, upload, reset };
}
